import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { addMatchRoster, listMatchRoster } from '../../../../lib/api/match';
import { listPlayersByClub } from '../../../../lib/api/license';

const getClubImageUrl = imagePath =>
  imagePath
    ? `${process.env.REACT_APP_BASE_API_URL}/uploads/${imagePath}`
    : null;

const formatDateValue = value => {
  if (!value) {
    return '';
  }

  const plainValue = String(value).split('T')[0];
  const [year, month, day] = plainValue.split('-');

  if (year && month && day) {
    return `${day}.${month}.${year}`;
  }

  return value;
};

const getPlayerRowId = item =>
  item?.entities_id ??
  item?.player_entities_id ??
  item?.player_guid ??
  item?.competitions_license_guid;

const toPlayerOption = item => ({
  value: getPlayerRowId(item),
  label: `${item.name || ''} ${item.last_name || ''} - ${item.selection_name || ''} - ${formatDateValue(
    item.date_of_birth
  )} ${item.competitions_name || ''}`.trim(),
  raw: item
});

const rosterSelectStyles = {
  placeholder: base => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  singleValue: base => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'calc(100% - 8px)'
  }),
  valueContainer: base => ({
    ...base,
    overflow: 'hidden'
  })
};

const PlayerRosterModal = ({ show, action, onClose, onSaved }) => {
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterPlayerOptions, setRosterPlayerOptions] = useState([]);
  const [selectedPlayerOption, setSelectedPlayerOption] = useState(null);
  const [rosterEntries, setRosterEntries] = useState([]);
  const [saveRosterLoading, setSaveRosterLoading] = useState(false);

  useEffect(() => {
    if (!show || !action || action.role !== 'player') {
      return;
    }

    const loadData = async () => {
      setRosterEntries([]);
      setSelectedPlayerOption(null);
      setRosterLoading(true);

      try {
        const [playersResponse, rosterResponse] = await Promise.all([
          listPlayersByClub(action.clubGuid, action.competitionsGuid),
          listMatchRoster({
            matches_guid: action.matchesGuid,
            role: action.role,
            club_guid: action.clubGuid
          })
        ]);

        const playerRows = Array.isArray(playersResponse)
          ? playersResponse
          : playersResponse?.items || [];
        const rosterRows = Array.isArray(rosterResponse)
          ? rosterResponse
          : rosterResponse?.items || [];

        setRosterPlayerOptions(playerRows.map(toPlayerOption));
        setRosterEntries(
          rosterRows
            .filter(
              row =>
                Number(row.club_entities_id) === Number(action.clubEntitiesId) &&
                row.team_side === action.teamSide &&
                row.role === action.role
            )
            .map(row => ({
              rowId: row.entities_id ?? row.match_roster_guid,
              ...row,
              selected_entities_id: row.entities_id,
              shirt_number:
                row.shirt_number === null || row.shirt_number === undefined
                  ? ''
                  : String(row.shirt_number),
              is_libero: Number(row.is_libero) === 1,
              is_captain: Number(row.is_captain) === 1
            }))
        );
      } catch (error) {
        console.error('Greska prilikom ucitavanja igraca za roster:', error);
        setRosterPlayerOptions([]);
        setRosterEntries([]);
      } finally {
        setRosterLoading(false);
      }
    };

    loadData();
  }, [show, action]);

  const availableRosterPlayerOptions = rosterPlayerOptions.filter(
    option =>
      !rosterEntries.some(
        entry => String(entry.rowId) === String(option.value)
      )
  );

  const handleRosterEntryChange = (entitiesId, key, value) => {
    setRosterEntries(prev =>
      prev.map(entry =>
        String(entry.rowId) === String(entitiesId)
          ? { ...entry, [key]: value }
          : entry
      )
    );
  };

  const handleShirtNumberChange = (rowId, value) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 2);
    handleRosterEntryChange(rowId, 'shirt_number', numericValue);
  };

  const handleShirtNumberKeyDown = (event, rowIndex) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    event.preventDefault();

    const nextIndex = event.key === 'ArrowDown' ? rowIndex + 1 : rowIndex - 1;
    const nextInput = document.querySelector(
      `[data-roster-shirt-input="${nextIndex}"]`
    );

    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  };

  const handlePlayerSelect = option => {
    setSelectedPlayerOption(option);

    if (!option?.raw) {
      return;
    }

    setRosterEntries(prev => {
      if (prev.some(entry => String(entry.rowId) === String(option.value))) {
        return prev;
      }

      return [
        ...prev,
        {
          rowId: option.value,
          ...option.raw,
          selected_entities_id: option.raw?.entities_id,
          shirt_number: '',
          is_libero: false,
          is_captain: false
        }
      ];
    });

    setSelectedPlayerOption(null);
  };

  const handleRemoveRosterEntry = entitiesId => {
    setRosterEntries(prev =>
      prev.filter(entry => String(entry.rowId) !== String(entitiesId))
    );
  };

  const handleClose = () => {
    setRosterPlayerOptions([]);
    setSelectedPlayerOption(null);
    setRosterEntries([]);
    setRosterLoading(false);
    setSaveRosterLoading(false);
    onClose();
  };

  const handleSaveRoster = async () => {
    if (!action || rosterEntries.length === 0) {
      handleClose();
      return;
    }

    const items = rosterEntries.map(entry => ({
      match_id: Number(action.matchId),
      club_entities_id: Number(action.clubEntitiesId),
      team_side: action.teamSide,
      role: action.role,
      entities_id: entry.selected_entities_id,
      shirt_number: entry.shirt_number === '' ? null : Number(entry.shirt_number),
      is_libero: entry.is_libero ? 1 : 0,
      is_captain: entry.is_captain ? 1 : 0
    }));
    const entityIds = items.map(item => String(item.entities_id));

    if (new Set(entityIds).size !== items.length) {
      toast.error(
        'Isti igrac je unesen vise puta (isti entities_id). Uklonite duplikat prije cuvanja.'
      );
      return;
    }

    if (rosterEntries.length > 14) {
      toast.error('Roster ne moze imati vise od 14 stavki.');
      return;
    }

    const nonLiberoCount = rosterEntries.filter(entry => !entry.is_libero).length;
    if (nonLiberoCount < 6) {
      toast.error('Morate unijeti minimum 6 igraca koji nisu libero.');
      return;
    }

    const liberoCount = rosterEntries.filter(entry => entry.is_libero).length;
    if (liberoCount > 2) {
      toast.error('Mogu biti oznacena najvise 2 libera.');
      return;
    }

    const captainCount = rosterEntries.filter(entry => entry.is_captain).length;
    if (captainCount !== 1) {
      toast.error('Mora biti oznacen tacno jedan kapiten.');
      return;
    }

    if (rosterEntries.some(entry => String(entry.shirt_number).trim() === '')) {
      toast.error('Shirt number je obavezan za sve stavke.');
      return;
    }

    const uniqueShirtNumbers = new Set(
      rosterEntries.map(entry => String(entry.shirt_number).trim())
    );
    if (uniqueShirtNumbers.size !== rosterEntries.length) {
      toast.error('Ne mogu postojati 2 ista shirt number.');
      return;
    }

    setSaveRosterLoading(true);

    try {
      await addMatchRoster({
        replace_role: action.role,
        items
      });

      if (onSaved) {
        await onSaved(action.matchesGuid);
      }

      toast.success('Roster je uspjesno sacuvan.');
      handleClose();
    } catch (error) {
      console.error('Greska prilikom cuvanja rostera:', error);
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Doslo je do greske prilikom cuvanja rostera.'
      );
    } finally {
      setSaveRosterLoading(false);
    }
  };

  return (
      <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton className="bg-200">
        <Modal.Title as="div" className="w-100 h5">
          <div className="d-flex align-items-center gap-2">
            {action?.clubImagePath && (
              <img
                src={getClubImageUrl(action.clubImagePath)}
                alt={action?.label || 'Club'}
                style={{
                  width: 28,
                  height: 28,
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
              />
            )}
            <span>{action?.label || ''}</span>
          </div>
          <div className="mt-2">
            <Badge
              className="badge rounded-pill bg-secondary fs-10"
              style={{ position: 'relative', top: 1 }}
            >
              {`${rosterEntries.length} igraca`}
            </Badge>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Izaberite igraca</Form.Label>
              <Select
                value={selectedPlayerOption}
                onChange={handlePlayerSelect}
                options={availableRosterPlayerOptions}
                isClearable
                isLoading={rosterLoading}
                classNamePrefix="react-select"
                styles={rosterSelectStyles}
                placeholder="Pretraga po imenu, prezimenu, datumu i takmicenju"
              />
            </Form.Group>
          </Col>
          <Col xs={12}>
            {rosterLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <>
                <div className="d-none d-md-block">
                  <Table responsive>
                    <thead className="bg-200">
                      <tr>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>#</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Igrac</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Selekcija</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Datum</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Broj dresa</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Libero</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Kapiten</th>
                        <th className="text-uppercase small text-muted text-end" style={{ paddingTop: 10, paddingBottom: 10 }}>Akcija</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rosterEntries.map((entry, index) => (
                        <tr key={entry.rowId}>
                          <td>{index + 1}</td>
                          <td>{`${entry.name || ''} ${entry.last_name || ''}`.trim()}</td>
                          <td>{entry.selection_name}</td>
                          <td>{formatDateValue(entry.date_of_birth)}</td>
                          <td style={{ minWidth: 140, paddingTop: 6, paddingBottom: 6 }}>
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              data-roster-shirt-input={index}
                              size="sm"
                              style={{
                                width: 80,
                                textAlign: 'right',
                                paddingTop: 4,
                                paddingBottom: 4,
                                fontWeight: 700
                              }}
                              value={entry.shirt_number}
                              onFocus={event => event.target.select()}
                              onKeyDown={event =>
                                handleShirtNumberKeyDown(event, index)
                              }
                              onChange={event =>
                                handleShirtNumberChange(entry.rowId, event.target.value)
                              }
                            />
                          </td>
                          <td className="text-center" style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <Form.Check
                              type="checkbox"
                              className="d-inline-block"
                              style={{ transform: 'scale(1.2)' }}
                              checked={Boolean(entry.is_libero)}
                              onChange={event =>
                                handleRosterEntryChange(
                                  entry.rowId,
                                  'is_libero',
                                  event.target.checked
                                )
                              }
                            />
                          </td>
                          <td className="text-center" style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <Form.Check
                              type="checkbox"
                              className="d-inline-block"
                              style={{ transform: 'scale(1.2)' }}
                              checked={Boolean(entry.is_captain)}
                              onChange={event =>
                                handleRosterEntryChange(
                                  entry.rowId,
                                  'is_captain',
                                  event.target.checked
                                )
                              }
                            />
                          </td>
                          <td className="text-end" style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="mb-1 btn btn-outline-danger"
                              onClick={() => handleRemoveRosterEntry(entry.rowId)}
                            >
                              Ukloni
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-md-none">
                  <Row className="g-3">
                    {rosterEntries.map((entry, index) => (
                      <Col xs={12} key={entry.rowId}>
                        <div
                          className="border rounded-4 bg-white h-100 p-3 shadow-sm"
                          style={{ borderColor: '#dfe3ee' }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div className="text-700 fs-10 fw-semibold">#{index + 1}</div>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger text-decoration-none"
                              onClick={() => handleRemoveRosterEntry(entry.rowId)}
                            >
                              <FontAwesomeIcon icon="trash-alt" />
                            </Button>
                          </div>

                          <div className="mt-3">
                            <div className="fw-semibold text-dark fs-8">
                              {`${entry.name || ''} ${entry.last_name || ''}`.trim()}
                            </div>
                            <div className="text-700 fs-10 mt-1">
                              {entry.selection_name || '-'}
                            </div>
                            <div className="text-600 fs-10 mt-1">
                              {formatDateValue(entry.date_of_birth) || '-'}
                            </div>
                          </div>

                          <Row className="g-3 mt-1">
                            <Col xs={12}>
                              <Form.Group>
                                <Form.Label className="small text-muted mb-1">Broj dresa</Form.Label>
                                <Form.Control
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  data-roster-shirt-input={index}
                                  style={{ textAlign: 'right', fontWeight: 700 }}
                                  value={entry.shirt_number}
                                  onFocus={event => event.target.select()}
                                  onKeyDown={event =>
                                    handleShirtNumberKeyDown(event, index)
                                  }
                                  onChange={event =>
                                    handleShirtNumberChange(entry.rowId, event.target.value)
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col xs={6}>
                              <Form.Check
                                type="checkbox"
                                label="Libero"
                                checked={Boolean(entry.is_libero)}
                                onChange={event =>
                                  handleRosterEntryChange(
                                    entry.rowId,
                                    'is_libero',
                                    event.target.checked
                                  )
                                }
                              />
                            </Col>
                            <Col xs={6}>
                              <Form.Check
                                type="checkbox"
                                label="Kapiten"
                                checked={Boolean(entry.is_captain)}
                                onChange={event =>
                                  handleRosterEntryChange(
                                    entry.rowId,
                                    'is_captain',
                                    event.target.checked
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Odustani
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveRoster}
          disabled={saveRosterLoading}
        >
          {saveRosterLoading ? 'Cuva se...' : 'Sacuvaj'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlayerRosterModal;
